<template>
    <div class="container flex flex-col">
        <div class="pt-3 pb-8">
            <h1 class="text-black text-xl lg:text-2xl mb-3">Factsheets</h1>
        </div>
        <div class="pb-8 w-1/6">
            <Search v-model="search" placeholder="Search all factsheets" />
        </div>
        <ResourceList :resources="filteredIndicatorResources" :endpoint="'indicator-resource'" />
    </div>
</template>

<script>
import Search from '@/components/partials/Search'
import ResourceList from '@/components/resource-list/ResourceList'
import { AuthMixin } from '@/components/shared/mixins/authHelpers'
import camelcaseKeys from 'camelcase-keys'
import GET_INDICATOR_RESOURCES_BY_SURVEY_EDITION from '@/graphql/queries/getIndicatorResourcesBySurveyEdition.gql'
import GET_SURVEY_VERSIONS_FOR_USER from '@/graphql/queries/getSurveyVersionsForUser.gql'
import RoleMixin from '@/components/shared/mixins/roleHelpers'
import { useDrf } from '@/services/featureFlags'
import { api } from '@/services/api'
import { DEFAULT_SURVEY_VERSION_EDITION_NUMBER } from '@/config/constants'

// Default practice types passed to the BySurveyEdition GraphQL
// query when no narrower set is known. Matches the value-set the
// BE viewset accepts on its survey_edition filter — the FE filter
// is identical on both paths.
const DEFAULT_PRACTICE_TYPES = ['GP', 'NON_GP']

export default {
    name: 'FactsheetsView',
    components: { Search, ResourceList },
    mixins: [ AuthMixin, RoleMixin ],
    data() {
        return {
            search: '',
            surveyVersions: [],
            indicatorResources: [],
        }
    },
    apollo: {
        indicatorResources: {
            query: GET_INDICATOR_RESOURCES_BY_SURVEY_EDITION,
            debounce: 200,
            variables() {
                return {
                    editionNumber: this.editionNumber,
                    practiceTypes: DEFAULT_PRACTICE_TYPES,
                    search: `%${this.search}%`,
                }
            },
            skip() {
                return !this.canViewResources || useDrf('getIndicatorResources')
            },
            // BySurveyEdition aliases the indicator_resource list as
            // `resourcesBySurveyEdition` — apollo property name
            // (`indicatorResources`) stays unchanged on the FE side.
            update({ resourcesBySurveyEdition }) {
                this.loading = false
                return camelcaseKeys(resourcesBySurveyEdition || [], { deep: true })
            },
            watchLoading (isLoading, countModifier) {
                this.loading = isLoading
            },
            error(error, vm, key, type, options) {
                this.error = error.message
            }
        },
        surveyVersions: {
            query: GET_SURVEY_VERSIONS_FOR_USER,
            variables() {
                return {
                    userId: this.userId
                }
            },
            skip() {
                // we want to run this if skipFiltered is false
                return !this.userId || !this.hasPracticeManagerRole || useDrf('getSurveyVersionsForUser')
            },
            update({ surveyVersions }) {
                // console.log('survey versions', surveyVersions)
                return camelcaseKeys(surveyVersions || {}, { deep: true })
            },
            watchLoading (isLoading, countModifier) {
                this.loading = isLoading
            },
            error(error, vm, key, type, options) {
                // for now, we just load it up with random data so we can see something
                this.error = error.message
            }
        },
    },
    mounted() {
        if (useDrf('getIndicatorResources') && this.canViewResources) {
            this.fetchIndicatorResourcesDrf()
        }
        if (useDrf('getSurveyVersionsForUser') && this.userId && this.hasPracticeManagerRole) {
            this.fetchSurveyVersionsDrf()
        }
    },
    methods: {
        async fetchIndicatorResourcesDrf() {
            this.loading = true
            try {
                // §H.1.3 — survey-edition scoped fetch. Sends the
                // BE viewset's `survey_edition` filter so the
                // response is already edition-scoped before it
                // reaches the FE-side role filter.
                this.indicatorResources = await api.get('web.indicatorresource-list/', {
                    search: this.search || undefined,
                    survey_edition: this.editionNumber,
                }) || []
            } catch (e) {
                this.error = e.message
            } finally {
                this.loading = false
            }
        },
        async fetchSurveyVersionsDrf() {
            this.loading = true
            try {
                this.surveyVersions = await api.get('web.surveyversion/for_user/') || []
            } catch (e) {
                this.error = e.message
            } finally {
                this.loading = false
            }
        },
    },
    computed: {
        // §H.1.3 — survey-edition for the BySurveyEdition query.
        // Reads the current edition from the user's first
        // accreditation when available (PM case where
        // surveyVersions is populated); otherwise falls back to
        // the platform default. Hardcoding the fallback to
        // DEFAULT_SURVEY_VERSION_EDITION_NUMBER means non-PM users
        // (surveyors / admins) get the current-edition resources,
        // which matches the previous behaviour where the .gql
        // returned all resources and the FE-side filter sorted
        // them out.
        editionNumber() {
            const first = this.surveyVersions
                && this.surveyVersions[0]
                && this.surveyVersions[0].accreditations
                && this.surveyVersions[0].accreditations[0]
                && this.surveyVersions[0].accreditations[0].surveyVersion
            if (first && first.editionNumber) {
                return first.editionNumber
            }
            return DEFAULT_SURVEY_VERSION_EDITION_NUMBER
        },
        surveyVersionIds() {
            if (!this.surveyVersions || !this.surveyVersions.length) {
                return null
            }

            return this.surveyVersions.reduce((arr, curr) => {
                if (curr.accreditations && curr.accreditations.length) {
                    const id = curr.accreditations[0].surveyVersion.id
                    if (id && !arr.includes(id)) {
                        arr.push(id)
                    }
                }
                return arr
            }, [])
        },
        filteredIndicatorResources() {
            if (!this.indicatorResources || !this.indicatorResources.length) {
                return []
            }

            return this.indicatorResources.filter(resource => {
                if (this.isAdmin) return true

                if (this.isSurveyor && resource.forSurveyor) {
                    return true
                }
                if (this.hasPracticeManagerRole && resource.forPm) {
                    return this.surveyVersionIds && resource.indicatorResourceIndicators.some(resourceIndicator => {
                        return this.surveyVersionIds.includes(resourceIndicator.indicator.surveyVersion.id)
                    })
                }

                return false
            })
        }
    },
    watch: {
        rolesLoaded(loaded) {
            if (loaded && !this.canViewResources) {
                this.$router.push('/Dashboard')
            }
            if (loaded && this.canViewResources && useDrf('getIndicatorResources')) {
                this.fetchIndicatorResourcesDrf()
            }
            if (loaded && this.hasPracticeManagerRole && useDrf('getSurveyVersionsForUser')) {
                this.fetchSurveyVersionsDrf()
            }
        },
        search() {
            if (useDrf('getIndicatorResources')) {
                this.fetchIndicatorResourcesDrf()
            }
        },
    }
}
</script>
